import type { Incident } from '../../incidents/types/incident.types'
import type {
  HeatmapCell,
  IncidentTypeData,
  KpiStat,
  MonthlyCompletedData,
  TeamTaskCountData,
} from '../types/analytics.types'
import { DAYS, INTENT_PALETTE, INTENT_TOP_N, TIME_SLOTS } from '../constants/analyticsConstants'

const UNASSIGNED = 'Unassigned'
const UNCLASSIFIED = 'Unclassified'
const OTHER = 'Other'
const MONTH_WINDOW = 6
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function isDone(incident: Incident): boolean {
  return incident.status === 'DONE'
}

function monthKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}`
}

export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds))
  return `${Math.floor(total / 60)}m ${total % 60}s`
}

export function computeAvgResponseTime(incidents: Incident[]): string {
  const durations: number[] = []
  for (const incident of incidents) {
    if (!incident.acknowledged_at) continue
    const diff =
      (new Date(incident.acknowledged_at).getTime() - new Date(incident.created_at).getTime()) / 1000
    durations.push(Math.max(0, diff))
  }
  if (durations.length === 0) return '—'
  const mean = durations.reduce((sum, value) => sum + value, 0) / durations.length
  return formatDuration(mean)
}

export function computeKpis(incidents: Incident[]): KpiStat[] {
  const done = incidents.filter(isDone).length
  const inProgress = incidents.filter((incident) => incident.status === 'IN_PROGRESS').length
  return [
    { label: 'Avg Response Time', value: computeAvgResponseTime(incidents), icon: 'timer', color: 'text-blue-500' },
    { label: 'Done', value: String(done), icon: 'check-circle', color: 'text-green-500' },
    { label: 'In Progress', value: String(inProgress), icon: 'clock', color: 'text-yellow-500' },
  ]
}

export function computeTasksByTeam(
  incidents: Incident[],
  teamNames: Map<number, string>,
): TeamTaskCountData[] {
  const counts = new Map<string, number>()
  for (const incident of incidents) {
    const label =
      incident.assignee_team_id === null
        ? UNASSIGNED
        : teamNames.get(incident.assignee_team_id) ?? `Team ${incident.assignee_team_id}`
    counts.set(label, (counts.get(label) ?? 0) + 1)
  }
  return [...counts.entries()]
    .map(([team, count]) => ({ team, count }))
    .sort((a, b) => b.count - a.count || a.team.localeCompare(b.team))
}

export function humanizeIntent(intent: string): string {
  return intent
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export function computeTasksByIntent(incidents: Incident[]): IncidentTypeData[] {
  if (incidents.length === 0) return []
  const counts = new Map<string, number>()
  for (const incident of incidents) {
    const raw = incident.intent?.trim()
    const key = raw ? raw : UNCLASSIFIED
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  const total = incidents.length
  const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
  const lastColor = INTENT_PALETTE[INTENT_PALETTE.length - 1]

  const result: IncidentTypeData[] = sorted.slice(0, INTENT_TOP_N).map(([key, value], index) => ({
    name: key === UNCLASSIFIED ? UNCLASSIFIED : humanizeIntent(key),
    value,
    percentage: Math.round((value / total) * 100),
    color: INTENT_PALETTE[index] ?? lastColor,
  }))

  const remainder = sorted.slice(INTENT_TOP_N).reduce((sum, [, value]) => sum + value, 0)
  if (remainder > 0) {
    result.push({
      name: OTHER,
      value: remainder,
      percentage: Math.round((remainder / total) * 100),
      color: lastColor,
    })
  }
  return result
}

export function computeCompletedByMonth(
  incidents: Incident[],
  referenceDate: Date,
): MonthlyCompletedData[] {
  const buckets: MonthlyCompletedData[] = []
  const indexByKey = new Map<string, number>()
  const refYear = referenceDate.getFullYear()
  const refMonth = referenceDate.getMonth()

  for (let offset = MONTH_WINDOW - 1; offset >= 0; offset--) {
    const date = new Date(refYear, refMonth - offset, 1)
    indexByKey.set(monthKey(date), buckets.length)
    buckets.push({ month: MONTH_LABELS[date.getMonth()], completed: 0, closed: 0 })
  }

  for (const incident of incidents) {
    if (isDone(incident)) {
      if (!incident.completed_at) continue
      const index = indexByKey.get(monthKey(new Date(incident.completed_at)))
      if (index !== undefined) buckets[index].completed += 1
    } else if (incident.status === 'CLOSED') {
      const stamp = incident.completed_at ?? incident.updated_at ?? incident.created_at
      const index = indexByKey.get(monthKey(new Date(stamp)))
      if (index !== undefined) buckets[index].closed += 1
    }
  }
  return buckets
}

export function timeSlotIndex(date: Date): number {
  const minutes = date.getHours() * 60 + date.getMinutes()
  if (minutes >= 420 && minutes <= 599) return 0 // 07:00 - 10:00
  if (minutes >= 600 && minutes <= 779) return 1 // 10:00 - 13:00
  if (minutes >= 780 && minutes <= 959) return 2 // 13:00 - 16:00
  if (minutes >= 960 && minutes <= 1139) return 3 // 16:00 - 19:00
  if (minutes >= 1140 && minutes <= 1319) return 4 // 19:00 - 22:00
  if (minutes >= 1320 || minutes <= 59) return 5 // 22:00 - 01:00
  if (minutes >= 60 && minutes <= 239) return 6 // 01:00 - 04:00
  return 7 // 04:00 - 07:00
}

export function dayIndex(date: Date): number {
  return (date.getDay() + 6) % 7
}

export function heatmapIntensity(count: number, weekMax: number): number {
  if (count <= 0) return 0
  const level = Math.ceil((count / Math.max(1, weekMax)) * 5)
  return Math.min(5, Math.max(1, level))
}

export function computeHeatmap(incidents: Incident[]): HeatmapCell[] {
  const tally: number[][] = TIME_SLOTS.map(() => DAYS.map(() => 0))
  for (const incident of incidents) {
    const date = new Date(incident.created_at)
    tally[timeSlotIndex(date)][dayIndex(date)] += 1
  }
  let weekMax = 0
  for (const row of tally) {
    for (const value of row) {
      if (value > weekMax) weekMax = value
    }
  }
  const cells: HeatmapCell[] = []
  TIME_SLOTS.forEach((timeSlot, slot) => {
    DAYS.forEach((day, dayCol) => {
      const count = tally[slot][dayCol]
      cells.push({ timeSlot, day, count, intensity: heatmapIntensity(count, weekMax) })
    })
  })
  return cells
}
